import type { Profile } from '../types/auth';

export type Action =
  | 'manage_members'
  | 'invite_household'
  | 'manage_billing'
  | 'regenerate_invite_code'
  | 'create_list'
  | 'delete_own_list'
  | 'delete_any_list'
  | 'add_inventory'
  | 'edit_inventory'
  | 'view_kitchen_budget'
  | 'add_calendar_event'
  | 'add_calendar_event_own'
  | 'post_memory'
  | 'post_memory_with_approval'
  | 'view_memory'
  | 'assign_chores'
  | 'complete_chores'
  | 'view_recipes'
  | 'edit_recipes'
  | 'view_family_settings'
  | 'manage_taxonomies'
  | 'edit_categories'
  | 'post_links';

type ProfileType = 'parent' | 'adult' | 'teen' | 'tween' | 'kid' | 'extended' | 'friend' | 'guest';

// Permission matrix — maps actions to which profile types can perform them
const PERMISSIONS: Record<Action, ProfileType[]> = {
  manage_members:            ['parent', 'adult'],
  invite_household:          ['parent', 'adult'],
  manage_billing:            ['parent', 'adult'],
  regenerate_invite_code:    ['parent', 'adult'],
  view_family_settings:      ['parent', 'adult', 'teen'],

  create_list:               ['parent', 'adult', 'teen', 'tween', 'kid'],
  delete_own_list:           ['parent', 'adult', 'teen'],
  delete_any_list:           ['parent'],

  add_inventory:             ['parent', 'adult'],
  edit_inventory:            ['parent', 'adult'],
  view_kitchen_budget:       ['parent', 'adult'],

  add_calendar_event:        ['parent', 'adult', 'teen'],
  add_calendar_event_own:    ['parent', 'adult', 'teen', 'tween', 'kid'],

  post_memory:               ['parent', 'adult', 'teen', 'extended'],
  post_memory_with_approval: ['parent', 'adult', 'teen', 'tween', 'kid'],
  view_memory:               ['parent', 'adult', 'teen', 'tween', 'kid', 'extended', 'friend'],

  assign_chores:             ['parent', 'adult'],
  complete_chores:           ['parent', 'adult', 'teen', 'tween', 'kid'],

  view_recipes:              ['parent', 'adult', 'teen', 'tween', 'kid'],
  edit_recipes:              ['parent', 'adult', 'teen'],
  manage_taxonomies:         ['parent', 'adult'],
  edit_categories:           ['parent', 'adult'],
  post_links:                ['parent', 'adult', 'teen'],
};

/**
 * Check if a profile can perform an action.
 */
export function can(profile: Profile | null | undefined, action: Action): boolean {
  if (!profile) return false;
  const type = getProfileType(profile);
  return PERMISSIONS[action]?.includes(type) ?? false;
}

/**
 * Check if a profile can see an item based on its visibility scope.
 * Scopes: 'direct' | 'family' | 'extended' | 'friends'
 */
export function canSee(profile: Profile | null | undefined, visibility: string, ownerId?: string): boolean {
  if (!profile) return false;

  // Direct/Private visibility: Only the owner sees it
  if (visibility === 'direct') {
    return profile.id === ownerId;
  }

  const type = getProfileType(profile);

  // Household members see everything except 'direct' (which is private/placeholder)
  const isHousehold = ['parent', 'adult', 'teen', 'tween', 'kid'].includes(type);
  if (isHousehold) return true;

  if (type === 'extended') {
    return ['extended', 'friends'].includes(visibility);
  }

  if (type === 'friend') {
    return visibility === 'friends';
  }

  return false;
}

/**
 * Returns the effective profile type, falling back to role-based inference
 * for backwards compatibility with existing data.
 */
export function getProfileType(profile: Profile): ProfileType {
  if (profile.profile_type) return profile.profile_type as ProfileType;

  // Legacy role fallback
  switch (profile.role) {
    case 'admin':  return 'parent';
    case 'member': return 'adult';
    case 'child':  return 'kid';
    case 'guest':  return 'guest';
    case 'viewer': return 'extended';
    default:       return 'adult';
  }
}

/**
 * Human-readable label for a profile type.
 */
export function getProfileTypeLabel(profile: Profile): string {
  const type = getProfileType(profile);
  switch (type) {
    case 'parent':   return 'Primary Organizer';
    case 'adult':    return 'Adult / Partner';
    case 'teen':     return 'Teen';
    case 'tween':    return 'Tween';
    case 'kid':      return 'Kid';
    case 'extended': return 'Extended Circle';
    case 'friend':   return 'Friend';
    case 'guest':    return 'Guest';
    default:         return 'Member';
  }
}

/**
 * Emoji badge for a profile type — used in member cards.
 */
export function getProfileTypeBadge(profile: Profile): string {
  const type = getProfileType(profile);
  switch (type) {
    case 'parent':   return '👑';
    case 'adult':    return '🏠';
    case 'teen':     return '🎧';
    case 'tween':    return '🎮';
    case 'kid':      return '⭐';
    case 'extended': return '🌳';
    case 'friend':   return '🤝';
    case 'guest':    return '👤';
    default:         return '👤';
  }
}

/**
 * Returns the best display name for a profile, in priority order:
 * nickname → display_name → first part of full_name → 'Member'
 */
export function getDisplayName(profile: Profile | null | undefined): string {
  if (!profile) return 'Member';
  return profile.nickname || profile.display_name || (profile.full_name || '').split(' ')[0] || 'Member';
}

/**
 * Returns a short name (nickname or first name) — used in tight spaces
 * like avatar tooltips, list activity, calendar events.
 */
export function getShortName(profile: Profile | null | undefined): string {
  if (!profile) return '?';
  return profile.nickname || profile.display_name || (profile.full_name || '').split(' ')[0] || '?';
}
